import type { ApiClient } from "../api-client";
import type { HttpTransport } from "../http/transport";
import type { FeatureSwitchList, FeatureSwitchView } from "../feature-switch-types";
import type { StyleExperimentResults, StyleExperimentView } from "../style-experiment-types";
import type {
  AccountStatementView,
  BalanceFollowUpSettingsView,
  RestaurantStatusRulesView,
  MenuSettingsView,
  ContactAccountView,
  RecordAccountEntryResult,
} from "../account-types";

type AccountsApiMethods = Pick<
  ApiClient,
  | "getContactAccount"
  | "recordAccountEntry"
  | "getAccountStatement"
  | "getAccountStatementPdf"
  | "getFeatureSwitches"
  | "setFeatureSwitch"
  | "listStyleExperiments"
  | "createStyleExperiment"
  | "getStyleExperimentResults"
  | "stopStyleExperiment"
  | "extendStyleExperiment"
  | "makeStyleExperimentWinner"
  | "getBalanceFollowUps"
  | "setBalanceFollowUps"
  | "getRestaurantStatusRules"
  | "getMenuSettings"
  | "setMenuUrl"
  | "uploadMenuMedia"
  | "deleteMenuMedia"
  | "refreshFoodMenu"
  | "setRestaurantStatusRule"
>;

export function createAccountsApi({
  request,
  ws,
  base: origin,
}: HttpTransport): AccountsApiMethods {
  const base = (id: string, contactId: string) =>
    ws(id) + "/contacts/" + encodeURIComponent(contactId) + "/account";
  const followUps = (id: string) => ws(id) + "/customer-accounts/balance-followups";
  const rules = (id: string) => ws(id) + "/restaurant-status-rules";
  const menu = (id: string) => ws(id) + "/menu-settings";
  return {
    getMenuSettings: (id) => request<MenuSettingsView>("GET", menu(id)),
    setMenuUrl: (id, menuUrl) => request<MenuSettingsView>("PUT", menu(id), { menuUrl }),
    uploadMenuMedia: (id, file) =>
      request<MenuSettingsView>("POST", menu(id) + "/media", undefined, {
        body: file,
        contentType: file.type || "application/octet-stream",
      }),
    deleteMenuMedia: (id) => request<MenuSettingsView>("DELETE", menu(id) + "/media"),
    refreshFoodMenu: (id) =>
      request<{ installed: number; changed: number }>("POST", menu(id) + "/refresh", {}),
    getRestaurantStatusRules: (id) => request<RestaurantStatusRulesView>("GET", rules(id)),
    setRestaurantStatusRule: (id, key, enabled) =>
      request<{ rules: RestaurantStatusRulesView["rules"] }>(
        "PUT",
        rules(id) + "/" + encodeURIComponent(key),
        { enabled },
      ),
    getFeatureSwitches: (id) => request<FeatureSwitchList>("GET", ws(id) + "/feature-switches"),
    setFeatureSwitch: (id, key, off) =>
      request<FeatureSwitchView>("PUT", ws(id) + "/feature-switches/" + encodeURIComponent(key), {
        off,
      }),
    listStyleExperiments: async (id, agentId) =>
      (
        await request<{ items: StyleExperimentView[] }>(
          "GET",
          ws(id) +
            "/style-experiments" +
            (agentId ? "?agentId=" + encodeURIComponent(agentId) : ""),
        )
      ).items,
    createStyleExperiment: (id, input) =>
      request<StyleExperimentView>("POST", ws(id) + "/style-experiments", input),
    getStyleExperimentResults: (id, expId) =>
      request<StyleExperimentResults>("GET", ws(id) + "/style-experiments/" + expId + "/results"),
    stopStyleExperiment: (id, expId) =>
      request<StyleExperimentView>("POST", ws(id) + "/style-experiments/" + expId + "/stop", {}),
    extendStyleExperiment: (id, expId, extraSample) =>
      request<StyleExperimentView>("POST", ws(id) + "/style-experiments/" + expId + "/extend", {
        extraSample,
      }),
    makeStyleExperimentWinner: (id, expId, variant) =>
      request<StyleExperimentView>(
        "POST",
        ws(id) + "/style-experiments/" + expId + "/make-winner",
        variant ? { variant } : {},
      ),
    getBalanceFollowUps: (id) => request<BalanceFollowUpSettingsView>("GET", followUps(id)),
    setBalanceFollowUps: (id, input) =>
      request<BalanceFollowUpSettingsView>("PUT", followUps(id), input),
    getContactAccount: (id, contactId) =>
      request<ContactAccountView>("GET", base(id, contactId) + "?limit=10"),
    recordAccountEntry: (id, contactId, input) =>
      request<RecordAccountEntryResult>("POST", base(id, contactId) + "/entries", input),
    getAccountStatement: (id, contactId, input) =>
      request<AccountStatementView>("POST", base(id, contactId) + "/statement", input ?? {}),
    async getAccountStatementPdf(id, contactId, lang) {
      const link = await request<{ url: string; filename: string; contentType: string }>(
        "POST",
        base(id, contactId) + "/statement?format=pdf&lang=" + lang,
        {},
      );
      const res = await globalThis.fetch(origin + link.url, { credentials: "include" });
      if (!res.ok) throw new Error("statement download failed");
      return new File([await res.blob()], link.filename, { type: link.contentType });
    },
  };
}
